import React from "react";
import type { ApolloClient } from "@apollo/client";
import { Formik } from "formik";
import type { FormikProps } from "formik";
import { Form } from "react-bootstrap";

import {
  ALPHANUMERIC_SPACES_MESSAGE,
  EmployerAddress1Validator,
  EmployerAddress2Validator,
  noWhitespaceRegex,
  postalCodeValidator,
  PRINTABLE_ASCII_MESSAGE,
  stateValidator,
  testAlphanumericSpaces,
  testEIN,
  testPrintableAsciiCharacters,
  validateUsersAddress,
  whiteSpaceError,
} from "utils/fieldValidators";
import { userService } from "services/userService";
import SimpleModal from "components/SimpleModal";
import AddressValidator from "components/AddressValidator";
import Button from "components/Button";

import AddressForm from "./forms/AddressForm";
import EinForm from "./forms/EinForm";
import BusinessNameForm from "./forms/BusinessNameForm";

import * as yup from "yup";

const createAddressSchema = () =>
  yup.object({
    address1: EmployerAddress1Validator,
    address2: EmployerAddress2Validator,
    city: yup
      .string()
      .label("City")
      .required()
      .test("city", PRINTABLE_ASCII_MESSAGE, testPrintableAsciiCharacters)
      .test("city", ALPHANUMERIC_SPACES_MESSAGE, testAlphanumericSpaces)
      .max(40)
      .matches(noWhitespaceRegex, whiteSpaceError),
    state: stateValidator,
    postalCode: postalCodeValidator,
  });

const createEinSchema = () =>
  yup.object({
    ein: yup
      .string()
      .label("EIN")
      .required()
      .test("ein", "${path} is not a valid format. Ex: 12-3456789", testEIN)
      .max(10),

    confirmEin: yup
      .string()
      .required()
      .label("Confirm EIN")
      .test("ein-match", "EIN values must match", function (value) {
        return this.parent.ein === value;
      }),
  });

const createBusinessNameSchema = () =>
  yup.object({
    businessName: yup
      .string()
      .label("Business Name")
      .required()
      .min(3, "Must be at least three characters.")
      .max(200, "Must be less than 200 characters."),

    confirmBusinessName: yup
      .string()
      .required()
      .label("Confirm Business Name")
      .test("name-match", "Business name values must match", function (value) {
        return this.parent.businessName === value;
      }),
  });

const FIELD_CONFIG = {
  address: {
    title: "Address",
    FormComponent: AddressForm,
  },
  ein: {
    title: "EIN",
    FormComponent: EinForm,
  },
  businessName: {
    title: "Business Name",
    FormComponent: BusinessNameForm,
  },
};

type FieldType = "address" | "ein" | "businessName";

interface AddressShape {
  address1?: string;
  address2?: string;
  city?: string;
  state?: string;
  postalCode?: string;
}

/**
 * The Formik values vary by `fieldType` (address / ein / businessName); all
 * fields are optional so a single shape covers every mode the form renders.
 * `initialValues` always seeds the relevant mode's fields as "" (strings).
 */
interface FormValues extends AddressShape {
  ein?: string;
  confirmEin?: string;
  businessName?: string;
  confirmBusinessName?: string;
}

interface Props {
  show: boolean;
  onClose: () => void;
  // Parent callback — this form does not dispatch; it hands the edited field
  // up to the parent. Each mode passes a distinct partial payload.
  onSubmit: (
    payload:
      | { ein: string }
      | { businessName: string }
      | { address: AddressShape }
  ) => void;
  businessName: string;
  client: ApolloClient<object>;
  fieldType: FieldType;
  currentAddress?: AddressShape;
  currentEin?: string;
  currentBusinessName?: string;
  isSubmitting?: boolean;
}

interface State {
  showAddressModal: boolean;
  fetchingAddress: boolean;
  validatedAddress: AddressShape;
  editedValues: FormValues | null;
}

class BusinessFieldEditModal extends React.PureComponent<Props, State> {
  state: State = {
    showAddressModal: false,
    fetchingAddress: false,
    validatedAddress: {},
    editedValues: null,
  };

  _formikHelpers: Pick<
    FormikProps<FormValues>,
    "setFieldValue" | "values"
  > | null = null;

  _getSchema = () => {
    const { fieldType, currentAddress, currentEin, currentBusinessName } =
      this.props;

    if (fieldType === "address") {
      // found-not-fixed: createAddressSchema takes no params; the `currentAddress || {}`
      // argument is silently ignored (BusinessFieldEditModal.tsx:30).
      // @ts-expect-error -- preserving the ignored-argument call (see found-not-fixed above)
      return createAddressSchema(currentAddress || {});
    }
    if (fieldType === "ein") {
      // found-not-fixed: createEinSchema takes no params; `currentEin` is silently
      // ignored (BusinessFieldEditModal.tsx:51).
      // @ts-expect-error -- preserving the ignored-argument call (see found-not-fixed above)
      return createEinSchema(currentEin);
    }
    // found-not-fixed: createBusinessNameSchema takes no params; `currentBusinessName`
    // is silently ignored (BusinessFieldEditModal.tsx:69).
    // @ts-expect-error -- preserving the ignored-argument call (see found-not-fixed above)
    return createBusinessNameSchema(currentBusinessName);
  };

  _getInitialValues = (): FormValues => {
    const { fieldType, currentAddress } = this.props;

    if (fieldType === "address") {
      return (
        this.state.editedValues || {
          address1: currentAddress?.address1 || "",
          address2: currentAddress?.address2 || "",
          city: currentAddress?.city || "",
          state: currentAddress?.state || "",
          postalCode: currentAddress?.postalCode || "",
        }
      );
    }
    if (fieldType === "ein") {
      return {
        ein: "",
        confirmEin: "",
      };
    }
    return {
      businessName: "",
      confirmBusinessName: "",
    };
  };

  _handleSubmit = async (values: FormValues, skipAddressValidation = false) => {
    const { fieldType } = this.props;

    if (fieldType === "address") {
      await this._handleAddressSubmit(values, skipAddressValidation);
    } else if (fieldType === "ein") {
      this.props.onSubmit({ ein: values.ein! });
    } else {
      this.props.onSubmit({ businessName: values.businessName! });
    }
  };

  _handleAddressSubmit = async (
    values: FormValues,
    skipAddressValidation = false
  ) => {
    if (!skipAddressValidation) {
      try {
        this.setState({ fetchingAddress: true });

        // In address mode the schema requires these fields, so they are always
        // populated strings at submit time, but FormValues types them optional.
        // The `!`s make the inferred shape satisfy both the query variables and
        // validateUsersAddress's AddressFields.
        const addressToValidate = {
          address1: values.address1!,
          address2: values.address2,
          city: values.city!,
          state: values.state!,
          postalCode: values.postalCode!,
        };

        // The query returns address fields as `string | null`; the rest of the
        // flow (validateUsersAddress, state, AddressValidator) is `string | undefined`.
        // null is falsy like undefined here, so coerce the shape at the boundary.
        const validatedAddress = (await userService.getValidatedAddress(
          this.props.client,
          addressToValidate
        )) as AddressShape | null;

        this.setState({ fetchingAddress: false });

        if (validatedAddress) {
          const userAddressIsValid = validateUsersAddress(
            addressToValidate,
            validatedAddress
          );

          if (!userAddressIsValid) {
            this.setState({
              showAddressModal: true,
              validatedAddress,
              editedValues: { ...values },
            });
            return;
          }
        }
      } catch (error) {
        console.log(error);
        this.setState({ fetchingAddress: false });
      }
    }

    this.props.onSubmit({
      address: {
        address1: values.address1,
        address2: values.address2,
        city: values.city,
        state: values.state,
        postalCode: values.postalCode,
      },
    });
  };

  _closeAddressModal = () => {
    this.setState({ showAddressModal: false });
  };

  render() {
    const { show, onClose, businessName, fieldType, isSubmitting } = this.props;
    const { title, FormComponent } = FIELD_CONFIG[fieldType];

    return (
      <>
        {this.state.showAddressModal && (
          <AddressValidator
            show={this.state.showAddressModal}
            onSubmit={this._handleSubmit}
            setFieldValue={this._formikHelpers?.setFieldValue}
            // state.validatedAddress is the closed AddressShape; AddressValidator's
            // prop adds an open `[key: string]: unknown` index signature. The
            // values are structurally identical address fields, so widen at the
            // boundary — runtime object is passed through unchanged.
            validatedAddress={
              this.state.validatedAddress as AddressShape & {
                [key: string]: unknown;
              }
            }
            onClose={this._closeAddressModal}
            values={{
              ...this._formikHelpers?.values,
              businessName,
            }}
            allowPoBox={true}
          />
        )}
        <SimpleModal
          show={show && !this.state.showAddressModal}
          onClose={onClose}
          title={`Edit ${title} for ${businessName}`}
          buttonText="Submit"
          hideActions
          showCloseButton
        >
          <Formik
            validateOnChange={false}
            validationSchema={this._getSchema()}
            onSubmit={(values) => this._handleSubmit(values)}
            initialValues={this._getInitialValues()}
            enableReinitialize={true}
          >
            {({
              handleSubmit,
              handleChange,
              handleBlur,
              values,
              touched,
              errors,
              setFieldValue,
            }) => {
              if (fieldType === "address") {
                this._formikHelpers = { setFieldValue, values };
              }
              return (
                <Form noValidate onSubmit={handleSubmit}>
                  {/* FormComponent is a union of the three field forms, whose
                      `values`/`touched`/`errors`/handler prop types differ; no
                      single prop set satisfies the union. The runtime FormComponent
                      always matches `fieldType`, so the props are correct in practice. */}
                  <FormComponent
                    handleChange={handleChange}
                    handleBlur={handleBlur}
                    // @ts-expect-error -- union FormComponent: FormValues (all fields optional) does not satisfy the intersected per-mode values type; runtime FormComponent always matches fieldType
                    values={values}
                    touched={touched}
                    errors={errors}
                  />
                  <div style={{ marginTop: 16, display: "flex", gap: 8 }}>
                    <Button
                      btnLabel="Cancel"
                      name="cancel"
                      type="button"
                      color="cancel"
                      onClick={onClose}
                    />
                    <Button
                      btnLabel="Submit for Approval"
                      name="submit"
                      type="submit"
                      loading={isSubmitting || this.state.fetchingAddress}
                      disabled={isSubmitting || this.state.fetchingAddress}
                    />
                  </div>
                </Form>
              );
            }}
          </Formik>
        </SimpleModal>
      </>
    );
  }
}

export default BusinessFieldEditModal;
